import { useState } from 'react';
import MainLayout from '../../../components/layout/MainLayout';
import { mockBillDetails } from './mockData';
import SearchFiltersComponent from './components/SearchFilters';
import type { BillDetail, SearchFilters } from './types';

export default function BillDetailsPage() {
  const [details] = useState<BillDetail[]>(mockBillDetails);
  const [filteredDetails, setFilteredDetails] = useState<BillDetail[]>(mockBillDetails);

  const handleSearch = (filters: SearchFilters) => {
    let filtered = [...details];

    if (filters.searchValue) {
      filtered = filtered.filter((detail) => {
        const value = filters.searchValue.toLowerCase();
        switch (filters.searchType) {
          case 'orderNo':
            return detail.orderNo.toLowerCase().includes(value);
          case 'transactionNo':
            return detail.transactionNo.toLowerCase().includes(value);
          case 'productName':
            return detail.productName.toLowerCase().includes(value);
          case 'tenantId':
            return detail.tenantId.toLowerCase().includes(value);
          case 'tenantName':
            return detail.tenantName.toLowerCase().includes(value);
          case 'userId':
            return detail.userId.toLowerCase().includes(value);
          case 'userName':
            return detail.userName.toLowerCase().includes(value);
          case 'organizationId':
            return detail.organizationId.toLowerCase().includes(value);
          case 'organizationName':
            return detail.organizationName.toLowerCase().includes(value);
          default:
            return true;
        }
      });
    }

    if (filters.transactionType) {
      filtered = filtered.filter((detail) => detail.transactionType === filters.transactionType);
    }

    if (filters.billMonth) {
      filtered = filtered.filter((detail) => detail.billMonth === filters.billMonth);
    }

    if (filters.startTime) {
      filtered = filtered.filter((detail) => detail.consumeTime >= filters.startTime.replace('T', ' '));
    }

    if (filters.endTime) {
      filtered = filtered.filter((detail) => detail.consumeTime <= filters.endTime.replace('T', ' '));
    }

    if (filters.paymentMethod) {
      filtered = filtered.filter((detail) => detail.paymentMethod === filters.paymentMethod);
    }

    if (filters.paymentType) {
      filtered = filtered.filter((detail) => detail.paymentType === filters.paymentType);
    }

    setFilteredDetails(filtered);
  };

  const getTransactionTypeColor = (type: string) => {
    switch (type) {
      case '新购':
        return 'text-green-600 bg-green-50';
      case '续购':
        return 'text-blue-600 bg-blue-50';
      case '自动续费':
        return 'text-purple-600 bg-purple-50';
      case '退订':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const renderResourceConfig = (config: BillDetail['resourceConfig']) => {
    const items = [];
    if (config.bandwidth) items.push(`宽带网络：${config.bandwidth}`);
    if (config.systemDiskType) items.push(`系统盘类型：${config.systemDiskType}`);
    if (config.systemDiskSize) items.push(`系统盘容量：${config.systemDiskSize}`);
    if (config.cpu) items.push(`CPU：${config.cpu}`);
    if (config.memory) items.push(`内存：${config.memory}`);
    if (config.gpu) items.push(`显卡：${config.gpu}`);
    if (config.dataDiskType) items.push(`数据盘类型：${config.dataDiskType}`);
    if (config.dataDiskSize) items.push(`数据盘容量：${config.dataDiskSize}`);
    return items.join(' | ');
  };

  return (
    <MainLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-800">账单明细</h1>
        <p className="text-sm text-gray-500 mt-1">查看所有用户的消费记录明细</p>
      </div>

      <SearchFiltersComponent onSearch={handleSearch} />

      <div className="bg-white rounded-lg border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  交易流水号
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  租户信息
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  用户信息
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  组织信息
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  订单号
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  账单月份
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  交易类型
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  付费方式
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  消费时间
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  服务时间
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  产品名称
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  资源配置
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  单价
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  数量
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  总金额
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  总积分
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  优惠金额
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  优惠积分
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  实付金额
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  实付积分
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  定价信息
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  时长信息
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                  支付方式
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredDetails.map((detail) => (
                <tr key={detail.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{detail.transactionNo}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {detail.tenantName !== '-' ? (
                      <div>
                        <div>{detail.tenantName}</div>
                        <div className="text-xs text-gray-500">{detail.tenantId}</div>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    <div>
                      <div>{detail.userName}</div>
                      <div className="text-xs text-gray-500">{detail.userId}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {detail.organizationName !== '-' ? (
                      <div>
                        <div>{detail.organizationName}</div>
                        <div className="text-xs text-gray-500">{detail.organizationId}</div>
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{detail.orderNo}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{detail.billMonth}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTransactionTypeColor(detail.transactionType)}`}>
                      {detail.transactionType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{detail.paymentMethod}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{detail.consumeTime}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    <div>{detail.serviceStartTime}</div>
                    <div>{detail.serviceEndTime}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{detail.productName}</td>
                  <td className="px-4 py-3 text-sm text-gray-600" style={{ maxWidth: '300px' }}>
                    {renderResourceConfig(detail.resourceConfig)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    ¥{Math.abs(detail.unitPrice).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{detail.quantity}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {detail.totalAmount !== 0 ? `¥${detail.totalAmount.toLocaleString()}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {detail.totalPoints !== 0 ? detail.totalPoints.toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {detail.discountAmount !== 0 ? `¥${detail.discountAmount.toLocaleString()}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {detail.discountPoints !== 0 ? detail.discountPoints.toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-teal-600 whitespace-nowrap">
                    {detail.actualAmount !== 0 ? `¥${detail.actualAmount.toLocaleString()}` : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-teal-600 whitespace-nowrap">
                    {detail.actualPoints !== 0 ? detail.actualPoints.toLocaleString() : '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    <div>{detail.pricingInfo.billingItemName}</div>
                    <div className="text-xs text-gray-500">{detail.pricingInfo.billingRuleDescription}</div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                    {detail.durationInfo.duration} {detail.durationInfo.durationUnit}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{detail.paymentType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredDetails.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-file-list-line text-4xl text-gray-300"></i>
            <p className="mt-2 text-sm text-gray-500">暂无账单明细</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
